"use strict";
/**
 * Dictation validation rules and checking utilities
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DICTATION_SCORING = void 0;
exports.checkDictation = checkDictation;
exports.validateDictation = validateDictation;
exports.getOptimalDictationSettings = getOptimalDictationSettings;
const textNormalization_1 = require("../utils/textNormalization");
/**
 * Dictation scoring configuration
 */
exports.DICTATION_SCORING = {
    // Base scoring
    PERFECT_SCORE: 100,
    // Error penalties
    WORD_ERROR_PENALTY: 5,
    PUNCTUATION_ERROR_PENALTY: 2,
    CAPITALIZATION_ERROR_PENALTY: 1,
    // Minimum passing scores by difficulty
    PASSING_SCORES: {
        EASY: 70,
        MEDIUM: 60,
        HARD: 50,
    },
    // Tolerance settings
    PARTIAL_MATCH_THRESHOLD: 0.8, // 80% similarity for partial credit
    MAX_ERRORS_FOR_PARTIAL: 3,
};
/**
 * Normalize text for dictation comparison
 */
function normalizeDictationText(text, options = {}) {
    const { ignorePunctuation = false, ignoreCapitalization = true, language: _language, } = options;
    // Basic normalization
    let normalized = (0, textNormalization_1.normalizeTextForComparison)(text);
    // Remove punctuation if specified
    if (ignorePunctuation) {
        normalized = normalized.replace(/[^\w\s]/g, "");
    }
    // Handle capitalization
    if (ignoreCapitalization) {
        normalized = normalized.toLowerCase();
    }
    // Clean up extra whitespace
    normalized = normalized.replace(/\s+/g, " ").trim();
    return normalized;
}
/**
 * Calculate Levenshtein distance between two strings
 */
function calculateLevenshteinDistance(str1, str2) {
    const matrix = [];
    const len1 = str1.length;
    const len2 = str2.length;
    // Initialize matrix
    for (let i = 0; i <= len1; i++) {
        matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
        matrix[0][j] = j;
    }
    // Fill matrix
    for (let i = 1; i <= len1; i++) {
        for (let j = 1; j <= len2; j++) {
            const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
            matrix[i][j] = Math.min(matrix[i - 1][j] + 1, // deletion
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j - 1] + cost);
        }
    }
    return matrix[len1][len2];
}
/**
 * Calculate similarity percentage between two texts
 */
function calculateSimilarity(original, userInput) {
    if (!original || !userInput)
        return 0;
    const distance = calculateLevenshteinDistance(original, userInput);
    const maxLength = Math.max(original.length, userInput.length);
    return maxLength === 0 ? 1 : (maxLength - distance) / maxLength;
}
/**
 * Count different types of errors
 */
function analyzeErrors(original, userInput, options = {}) {
    const { language } = options;
    // Normalize for word comparison
    const originalWords = normalizeDictationText(original, {
        ignorePunctuation: true,
        ignoreCapitalization: true,
        language,
    }).split(/\s+/);
    const userWords = normalizeDictationText(userInput, {
        ignorePunctuation: true,
        ignoreCapitalization: true,
        language,
    }).split(/\s+/);
    // Count word errors (simplified - actual words that don't match)
    const _maxWords = Math.max(originalWords.length, userWords.length);
    let wordErrors = Math.abs(originalWords.length - userWords.length);
    for (let i = 0; i < Math.min(originalWords.length, userWords.length); i++) {
        if (originalWords[i] !== userWords[i]) {
            wordErrors++;
        }
    }
    // Count punctuation errors
    const originalPunct = (original.match(/[^\w\s]/g) || []).join("");
    const userPunct = (userInput.match(/[^\w\s]/g) || []).join("");
    const punctuationErrors = calculateLevenshteinDistance(originalPunct, userPunct);
    // Count capitalization errors (compare original case with user case, ignoring punctuation)
    const originalCaseOnly = original.replace(/[^\w\s]/g, "");
    const userCaseOnly = userInput.replace(/[^\w\s]/g, "");
    const originalLower = originalCaseOnly.toLowerCase();
    const userLower = userCaseOnly.toLowerCase();
    let capitalizationErrors = 0;
    if (originalLower === userLower) {
        // Same content, count case differences
        for (let i = 0; i < Math.min(originalCaseOnly.length, userCaseOnly.length); i++) {
            if (originalCaseOnly[i] !== userCaseOnly[i]) {
                capitalizationErrors++;
            }
        }
    }
    return {
        wordErrors,
        punctuationErrors,
        capitalizationErrors,
        totalErrors: wordErrors + punctuationErrors + capitalizationErrors,
    };
}
/**
 * Check dictation answer and calculate score
 */
function checkDictation(originalText, userInput, difficulty, options = {}) {
    if (!originalText || !userInput) {
        return {
            score: 0,
            isCorrect: false,
            similarity: 0,
            errors: {
                wordErrors: 0,
                punctuationErrors: 0,
                capitalizationErrors: 0,
                totalErrors: 0,
            },
            feedback: "Please provide both original text and user input",
        };
    }
    // Normalize texts for comparison
    const normalizedOriginal = normalizeDictationText(originalText, options);
    const normalizedInput = normalizeDictationText(userInput, options);
    // Check for exact match
    const isExactMatch = normalizedOriginal === normalizedInput;
    if (isExactMatch) {
        return {
            score: exports.DICTATION_SCORING.PERFECT_SCORE,
            isCorrect: true,
            similarity: 1,
            errors: {
                wordErrors: 0,
                punctuationErrors: 0,
                capitalizationErrors: 0,
                totalErrors: 0,
            },
            feedback: "Perfect!",
        };
    }
    // Analyze errors
    const errors = analyzeErrors(originalText, userInput, options);
    const similarity = calculateSimilarity(normalizedOriginal, normalizedInput);
    // Calculate score based on errors and difficulty
    let score = exports.DICTATION_SCORING.PERFECT_SCORE;
    score -= errors.wordErrors * exports.DICTATION_SCORING.WORD_ERROR_PENALTY;
    score -=
        errors.punctuationErrors * exports.DICTATION_SCORING.PUNCTUATION_ERROR_PENALTY;
    score -=
        errors.capitalizationErrors *
            exports.DICTATION_SCORING.CAPITALIZATION_ERROR_PENALTY;
    // Apply difficulty-based adjustment
    const difficultyMultiplier = {
        EASY: 0.8, // More forgiving
        MEDIUM: 1.0, // Standard
        HARD: 1.2, // More strict
    }[difficulty];
    const adjustedPenalty = (exports.DICTATION_SCORING.PERFECT_SCORE - score) * difficultyMultiplier;
    score = Math.max(0, exports.DICTATION_SCORING.PERFECT_SCORE - adjustedPenalty);
    // Partial credit for high similarity
    if (options.allowPartialCredit &&
        similarity >= exports.DICTATION_SCORING.PARTIAL_MATCH_THRESHOLD &&
        errors.totalErrors <= exports.DICTATION_SCORING.MAX_ERRORS_FOR_PARTIAL) {
        score = Math.max(score, similarity * exports.DICTATION_SCORING.PERFECT_SCORE * 0.7);
    }
    // Round score
    score = Math.round(score);
    const passingScore = exports.DICTATION_SCORING.PASSING_SCORES[difficulty];
    const isCorrect = score >= passingScore;
    // Generate feedback
    let feedback = "";
    if (isCorrect) {
        if (score >= 90)
            feedback = "Excellent work!";
        else if (score >= 80)
            feedback = "Good job!";
        else
            feedback = "Well done!";
    }
    else {
        feedback = `Score too low (${score}/${passingScore} required). `;
        if (errors.wordErrors > 0)
            feedback += `Word errors: ${errors.wordErrors}. `;
        if (errors.punctuationErrors > 0)
            feedback += `Punctuation errors: ${errors.punctuationErrors}. `;
        if (errors.capitalizationErrors > 0)
            feedback += `Capitalization errors: ${errors.capitalizationErrors}.`;
    }
    return {
        score,
        isCorrect,
        similarity,
        errors,
        feedback: feedback.trim(),
    };
}
/**
 * Validate dictation setup before starting
 */
function validateDictation(text, difficulty, language) {
    const errors = [];
    const warnings = [];
    // Basic validation
    if (!text || text.trim().length === 0) {
        errors.push("Dictation text cannot be empty");
    }
    if (text.length < 10) {
        warnings.push("Text is very short for dictation practice");
    }
    if (text.length > 500) {
        warnings.push("Text might be too long for effective dictation");
    }
    // Language-specific checks
    if (language) {
        const isCJK = ["zh", "ja", "ko"].includes(language);
        if (isCJK && text.length > 200) {
            warnings.push("CJK text should be shorter for dictation exercises");
        }
    }
    // Difficulty appropriateness
    const wordCount = text.split(/\s+/).length;
    const difficultyRanges = {
        EASY: { min: 5, max: 30 },
        MEDIUM: { min: 15, max: 60 },
        HARD: { min: 30, max: 100 },
    };
    const range = difficultyRanges[difficulty];
    if (wordCount < range.min) {
        warnings.push(`Text is short for ${difficulty} difficulty (${wordCount} words, recommended: ${range.min}-${range.max})`);
    }
    else if (wordCount > range.max) {
        warnings.push(`Text is long for ${difficulty} difficulty (${wordCount} words, recommended: ${range.min}-${range.max})`);
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        recommendations: [
            "Ensure text has clear pronunciation",
            "Consider adding punctuation marks for practice",
            "Test audio quality before starting session",
        ],
    };
}
/**
 * Get optimal dictation settings for a language
 */
function getOptimalDictationSettings(language) {
    const _isCJK = ["zh", "ja", "ko"].includes(language);
    return {
        ignorePunctuation: false, // Generally keep punctuation for learning
        ignoreCapitalization: !["de"].includes(language), // German needs capitalization
        allowPartialCredit: true,
        language,
    };
}
//# sourceMappingURL=dictationValidation.js.map